import type { D1Database } from '@cloudflare/workers-types';

import { FeatureFlagName, User, UserRole } from '../types/models';

export interface PermissionContext {
	user: User;
	resourceUserId?: string;
	resourceWorkOrderId?: string;
	resourceAssociationId?: string;
	resourceTaskId?: string;
}

export enum Permission {
	// User management
	VIEW_USERS = 'view_users',
	CREATE_USERS = 'create_users',
	UPDATE_USERS = 'update_users',
	DELETE_USERS = 'delete_users',

	// Work orders
	VIEW_WORK_ORDERS = 'view_work_orders',
	CREATE_WORK_ORDERS = 'create_work_orders',
	UPDATE_WORK_ORDERS = 'update_work_orders',
	DELETE_WORK_ORDERS = 'delete_work_orders',

	/**
	 * Write the customer-facing description and declare a work order ready for the PM to
	 * approve (#496). Accounting's review pass — the step that replaced printing a sheet
	 * and walking it round the office.
	 *
	 * Its own permission because `UPDATE_WORK_ORDERS` is wrong in both directions. It is
	 * far too broad: property managers hold it, and a PM who can mark their own work
	 * orders ready can approve them in the same breath, skipping the sanitising pass
	 * entirely — the SLA would be met and the customer would still read "dumb customer" on
	 * their invoice. It is also not quite the right shape, since this gate governs one
	 * field and one transition rather than the record.
	 *
	 * Granted to Admin and Accounting only. Note this is a ROLE floor, deliberately
	 * separate from the `accounting` feature flag: the flag decides who can open the
	 * queue page, this decides who can move the state. A capability flag with no role
	 * floor behind it outranks the matrix, which is how a gate stops meaning anything.
	 */
	REVIEW_WORK_ORDERS = 'review_work_orders',

	// Narrow people-write exception: fill a work order's matched-account owner
	// contact — set a MISSING owner phone, or create the owner when none exists.
	// The server enforces fill-a-blank-only (POST /accounts/:id/owner-contact),
	// so this is safe to grant to roles that otherwise can't write people /
	// accounts (e.g. Property Managers). Issue #285.
	MANAGE_WORK_ORDER_OWNER_CONTACT = 'manage_work_order_owner_contact',

	// Exterior Improvement Applications (architectural review) — #363
	VIEW_EXTERIOR_APPLICATIONS = 'view_exterior_applications',
	CREATE_EXTERIOR_APPLICATIONS = 'create_exterior_applications',
	UPDATE_EXTERIOR_APPLICATIONS = 'update_exterior_applications',
	DELETE_EXTERIOR_APPLICATIONS = 'delete_exterior_applications',

	// Associations
	VIEW_ASSOCIATIONS = 'view_associations',
	CREATE_ASSOCIATIONS = 'create_associations',
	UPDATE_ASSOCIATIONS = 'update_associations',
	DELETE_ASSOCIATIONS = 'delete_associations',

	/**
	 * File a document into an association's or an owner's document folder.
	 *
	 * Its own permission because neither neighbour fits. Reading the document browser is
	 * `VIEW_ASSOCIATIONS`, which every role holds — that is what let a Contractor upload.
	 * But `UPDATE_ASSOCIATIONS` is far too broad in the other direction: Support has it
	 * off (they do not edit association records) yet filing documents is their daily
	 * work, so gating uploads on it locks out the people who do the job.
	 */
	UPLOAD_DOCUMENTS = 'upload_documents',

	/**
	 * Reshape the document store: which drive folder is a community's, which roots are
	 * hidden, and what each folder looks like.
	 *
	 * Separate from `UPLOAD_DOCUMENTS` because the blast radius is completely different.
	 * Uploading files is daily work for nearly everyone; this decides where an entire
	 * community's documents are READ AND WRITTEN from, and hiding a root refuses browse,
	 * download and search for everyone at once. Mis-set, it takes a community's filing
	 * offline rather than adding one wrong file.
	 *
	 * Off for every role by default: the Admin role holds it (a bypass, not a matrix row), and
	 * everyone else only through the `association_admin` capability. Named rather than left as a
	 * `role === 'admin'` comparison so it could be granted per-user without touching call sites
	 * — which is the whole point of the #500 model: every elevated power spelled out.
	 */
	MANAGE_DOCUMENT_STRUCTURE = 'manage_document_structure',

	// Time entries
	VIEW_TIME_ENTRIES = 'view_time_entries',
	CREATE_TIME_ENTRIES = 'create_time_entries',
	UPDATE_TIME_ENTRIES = 'update_time_entries',
	DELETE_TIME_ENTRIES = 'delete_time_entries',

	// Materials
	VIEW_MATERIALS = 'view_materials',
	CREATE_MATERIALS = 'create_materials',
	UPDATE_MATERIALS = 'update_materials',
	DELETE_MATERIALS = 'delete_materials',

	// Material costs
	VIEW_MATERIAL_COSTS = 'view_material_costs',
	CREATE_MATERIAL_COSTS = 'create_material_costs',
	UPDATE_MATERIAL_COSTS = 'update_material_costs',
	DELETE_MATERIAL_COSTS = 'delete_material_costs',

	// Misc costs
	VIEW_MISC_COSTS = 'view_misc_costs',
	CREATE_MISC_COSTS = 'create_misc_costs',
	UPDATE_MISC_COSTS = 'update_misc_costs',
	DELETE_MISC_COSTS = 'delete_misc_costs',

	// Location events
	VIEW_LOCATION_EVENTS = 'view_location_events',
	CREATE_LOCATION_EVENTS = 'create_location_events',
	UPDATE_LOCATION_EVENTS = 'update_location_events',
	DELETE_LOCATION_EVENTS = 'delete_location_events',

	// Inspection issues
	VIEW_INSPECTION_ISSUES = 'view_inspection_issues',
	CREATE_INSPECTION_ISSUES = 'create_inspection_issues',
	UPDATE_INSPECTION_ISSUES = 'update_inspection_issues',
	DELETE_INSPECTION_ISSUES = 'delete_inspection_issues',

	// Inspection reports
	VIEW_INSPECTION_REPORTS = 'view_inspection_reports',
	CREATE_INSPECTION_REPORTS = 'create_inspection_reports',
	UPDATE_INSPECTION_REPORTS = 'update_inspection_reports',
	DELETE_INSPECTION_REPORTS = 'delete_inspection_reports',

	// Inspection report issues
	VIEW_INSPECTION_REPORT_ISSUES = 'view_inspection_report_issues',
	CREATE_INSPECTION_REPORT_ISSUES = 'create_inspection_report_issues',
	UPDATE_INSPECTION_REPORT_ISSUES = 'update_inspection_report_issues',
	DELETE_INSPECTION_REPORT_ISSUES = 'delete_inspection_report_issues',

	// History
	VIEW_HISTORY = 'view_history',
	COMMENT_HISTORY = 'comment_history',

	// Association shortcut codes
	VIEW_ASSOCIATION_SHORTCUT_CODES = 'view_association_shortcut_codes',
	CREATE_ASSOCIATION_SHORTCUT_CODES = 'create_association_shortcut_codes',
	UPDATE_ASSOCIATION_SHORTCUT_CODES = 'update_association_shortcut_codes',
	DELETE_ASSOCIATION_SHORTCUT_CODES = 'delete_association_shortcut_codes',

	// User impersonation
	IMPERSONATE_USERS = 'impersonate_users',

	// Tasks
	VIEW_TASKS = 'view_tasks',
	CREATE_TASKS = 'create_tasks',
	UPDATE_TASKS = 'update_tasks',
	DELETE_TASKS = 'delete_tasks',

	// Accounting — people (non-login contacts)
	VIEW_PEOPLE = 'view_people',
	CREATE_PEOPLE = 'create_people',
	UPDATE_PEOPLE = 'update_people',
	DELETE_PEOPLE = 'delete_people',

	// Accounting — accounts
	VIEW_ACCOUNTS = 'view_accounts',
	CREATE_ACCOUNTS = 'create_accounts',
	UPDATE_ACCOUNTS = 'update_accounts',
	DELETE_ACCOUNTS = 'delete_accounts',

	// Accounting — money movements
	VIEW_CHARGES = 'view_charges',
	CREATE_CHARGES = 'create_charges',
	UPDATE_CHARGES = 'update_charges',
	DELETE_CHARGES = 'delete_charges',

	VIEW_PAYMENTS = 'view_payments',
	CREATE_PAYMENTS = 'create_payments',
	UPDATE_PAYMENTS = 'update_payments',
	DELETE_PAYMENTS = 'delete_payments',

	VIEW_CREDITS = 'view_credits',
	CREATE_CREDITS = 'create_credits',
	UPDATE_CREDITS = 'update_credits',
	DELETE_CREDITS = 'delete_credits',

	VIEW_REFUNDS = 'view_refunds',
	CREATE_REFUNDS = 'create_refunds',
	UPDATE_REFUNDS = 'update_refunds',
	DELETE_REFUNDS = 'delete_refunds',

	// Accounting — automation
	VIEW_ASSESSMENT_SCHEDULES = 'view_assessment_schedules',
	CREATE_ASSESSMENT_SCHEDULES = 'create_assessment_schedules',
	UPDATE_ASSESSMENT_SCHEDULES = 'update_assessment_schedules',
	DELETE_ASSESSMENT_SCHEDULES = 'delete_assessment_schedules',

	VIEW_LATE_FEE_RULES = 'view_late_fee_rules',
	CREATE_LATE_FEE_RULES = 'create_late_fee_rules',
	UPDATE_LATE_FEE_RULES = 'update_late_fee_rules',
	DELETE_LATE_FEE_RULES = 'delete_late_fee_rules',

	// Accounting — notes
	VIEW_ACCOUNT_NOTES = 'view_account_notes',
	CREATE_ACCOUNT_NOTES = 'create_account_notes',
	UPDATE_ACCOUNT_NOTES = 'update_account_notes',
	DELETE_ACCOUNT_NOTES = 'delete_account_notes',

	// Accounting — reports
	VIEW_ACCOUNTING_REPORTS = 'view_accounting_reports',

	// Accounting — GL codes (Chart of Accounts)
	VIEW_GL_CODES = 'view_gl_codes',
	CREATE_GL_CODES = 'create_gl_codes',
	UPDATE_GL_CODES = 'update_gl_codes',
	DELETE_GL_CODES = 'delete_gl_codes',

	// Accounting — vendors (external payees)
	VIEW_VENDORS = 'view_vendors',
	CREATE_VENDORS = 'create_vendors',
	UPDATE_VENDORS = 'update_vendors',
	DELETE_VENDORS = 'delete_vendors',

	// Accounting — bank accounts (per-association checking/MM/savings)
	VIEW_BANK_ACCOUNTS = 'view_bank_accounts',
	CREATE_BANK_ACCOUNTS = 'create_bank_accounts',
	UPDATE_BANK_ACCOUNTS = 'update_bank_accounts',
	DELETE_BANK_ACCOUNTS = 'delete_bank_accounts',

	// Accounting — bills (AP) and bill payments
	VIEW_BILLS = 'view_bills',
	CREATE_BILLS = 'create_bills',
	UPDATE_BILLS = 'update_bills',
	DELETE_BILLS = 'delete_bills',
	PAY_BILLS = 'pay_bills',

	// Accounting — invoices (AR wrappers around charges)
	VIEW_INVOICES = 'view_invoices',
	CREATE_INVOICES = 'create_invoices',
	UPDATE_INVOICES = 'update_invoices',
	DELETE_INVOICES = 'delete_invoices',

	// Accounting — bank reconciliations
	VIEW_RECONCILIATIONS = 'view_reconciliations',
	CREATE_RECONCILIATIONS = 'create_reconciliations',
	UPDATE_RECONCILIATIONS = 'update_reconciliations',
	DELETE_RECONCILIATIONS = 'delete_reconciliations',

	// Accounting — manual / adjusting journal entries
	VIEW_JOURNAL_ENTRIES = 'view_journal_entries',
	CREATE_JOURNAL_ENTRIES = 'create_journal_entries',
	UPDATE_JOURNAL_ENTRIES = 'update_journal_entries',
	DELETE_JOURNAL_ENTRIES = 'delete_journal_entries',

	// Accounting — annual budgets per association / fund
	VIEW_BUDGETS = 'view_budgets',
	CREATE_BUDGETS = 'create_budgets',
	UPDATE_BUDGETS = 'update_budgets',
	DELETE_BUDGETS = 'delete_budgets',

	// Accounting — bank statement imports + auto-match
	VIEW_BANK_IMPORTS = 'view_bank_imports',
	MANAGE_BANK_IMPORTS = 'manage_bank_imports',

	// Accounting — collections workflow (delinquency timeline)
	VIEW_COLLECTIONS = 'view_collections',
	MANAGE_COLLECTIONS = 'manage_collections',

	// Accounting — invoice line-item presets (QB-style Item List)
	VIEW_ITEMS = 'view_items',
	CREATE_ITEMS = 'create_items',
	UPDATE_ITEMS = 'update_items',
	DELETE_ITEMS = 'delete_items',
}

const DEFAULT_PERMISSIONS: Record<Permission, boolean> = {
	[Permission.VIEW_USERS]: true,
	[Permission.CREATE_USERS]: true,
	[Permission.UPDATE_USERS]: true,
	[Permission.DELETE_USERS]: true,
	[Permission.VIEW_WORK_ORDERS]: true,
	[Permission.CREATE_WORK_ORDERS]: true,
	[Permission.UPDATE_WORK_ORDERS]: true,
	[Permission.DELETE_WORK_ORDERS]: true,
	// True HERE because this map is the admin baseline every role spreads and then
	// subtracts from — the four roles that must not review turn it off explicitly below.
	[Permission.REVIEW_WORK_ORDERS]: true,
	[Permission.MANAGE_WORK_ORDER_OWNER_CONTACT]: true,
	[Permission.VIEW_EXTERIOR_APPLICATIONS]: true,
	[Permission.CREATE_EXTERIOR_APPLICATIONS]: true,
	[Permission.UPDATE_EXTERIOR_APPLICATIONS]: true,
	[Permission.DELETE_EXTERIOR_APPLICATIONS]: true,
	[Permission.VIEW_ASSOCIATIONS]: true,
	[Permission.CREATE_ASSOCIATIONS]: true,
	[Permission.UPDATE_ASSOCIATIONS]: true,
	[Permission.DELETE_ASSOCIATIONS]: true,
	[Permission.UPLOAD_DOCUMENTS]: true,
	// FALSE by default, unlike almost everything else here: reshaping the document store is
	// an Admin-role power (the Admin role bypasses this matrix entirely) granted to others only
	// through `association_admin`. Granting it to a whole role would be a deliberate diff line.
	[Permission.MANAGE_DOCUMENT_STRUCTURE]: false,
	[Permission.VIEW_TIME_ENTRIES]: true,
	[Permission.CREATE_TIME_ENTRIES]: true,
	[Permission.UPDATE_TIME_ENTRIES]: true,
	[Permission.DELETE_TIME_ENTRIES]: true,
	[Permission.VIEW_MATERIALS]: true,
	[Permission.CREATE_MATERIALS]: true,
	[Permission.UPDATE_MATERIALS]: true,
	[Permission.DELETE_MATERIALS]: true,
	[Permission.VIEW_MATERIAL_COSTS]: true,
	[Permission.CREATE_MATERIAL_COSTS]: true,
	[Permission.UPDATE_MATERIAL_COSTS]: true,
	[Permission.DELETE_MATERIAL_COSTS]: true,
	[Permission.VIEW_MISC_COSTS]: true,
	[Permission.CREATE_MISC_COSTS]: true,
	[Permission.UPDATE_MISC_COSTS]: true,
	[Permission.DELETE_MISC_COSTS]: true,
	[Permission.VIEW_LOCATION_EVENTS]: true,
	[Permission.CREATE_LOCATION_EVENTS]: true,
	[Permission.UPDATE_LOCATION_EVENTS]: true,
	[Permission.DELETE_LOCATION_EVENTS]: true,
	[Permission.VIEW_INSPECTION_ISSUES]: true,
	[Permission.CREATE_INSPECTION_ISSUES]: true,
	[Permission.UPDATE_INSPECTION_ISSUES]: true,
	[Permission.DELETE_INSPECTION_ISSUES]: true,
	[Permission.VIEW_INSPECTION_REPORTS]: true,
	[Permission.CREATE_INSPECTION_REPORTS]: true,
	[Permission.UPDATE_INSPECTION_REPORTS]: true,
	[Permission.DELETE_INSPECTION_REPORTS]: true,
	[Permission.VIEW_INSPECTION_REPORT_ISSUES]: true,
	[Permission.CREATE_INSPECTION_REPORT_ISSUES]: true,
	[Permission.UPDATE_INSPECTION_REPORT_ISSUES]: true,
	[Permission.DELETE_INSPECTION_REPORT_ISSUES]: true,
	[Permission.VIEW_HISTORY]: true,
	[Permission.COMMENT_HISTORY]: true,
	[Permission.VIEW_ASSOCIATION_SHORTCUT_CODES]: true,
	[Permission.CREATE_ASSOCIATION_SHORTCUT_CODES]: true,
	[Permission.UPDATE_ASSOCIATION_SHORTCUT_CODES]: true,
	[Permission.DELETE_ASSOCIATION_SHORTCUT_CODES]: true,
	[Permission.IMPERSONATE_USERS]: true,
	[Permission.VIEW_TASKS]: true,
	[Permission.CREATE_TASKS]: true,
	[Permission.UPDATE_TASKS]: true,
	[Permission.DELETE_TASKS]: true,

	// Accounting
	[Permission.VIEW_PEOPLE]: true,
	[Permission.CREATE_PEOPLE]: true,
	[Permission.UPDATE_PEOPLE]: true,
	[Permission.DELETE_PEOPLE]: true,
	[Permission.VIEW_ACCOUNTS]: true,
	[Permission.CREATE_ACCOUNTS]: true,
	[Permission.UPDATE_ACCOUNTS]: true,
	[Permission.DELETE_ACCOUNTS]: true,
	[Permission.VIEW_CHARGES]: true,
	[Permission.CREATE_CHARGES]: true,
	[Permission.UPDATE_CHARGES]: true,
	[Permission.DELETE_CHARGES]: true,
	[Permission.VIEW_PAYMENTS]: true,
	[Permission.CREATE_PAYMENTS]: true,
	[Permission.UPDATE_PAYMENTS]: true,
	[Permission.DELETE_PAYMENTS]: true,
	[Permission.VIEW_CREDITS]: true,
	[Permission.CREATE_CREDITS]: true,
	[Permission.UPDATE_CREDITS]: true,
	[Permission.DELETE_CREDITS]: true,
	[Permission.VIEW_REFUNDS]: true,
	[Permission.CREATE_REFUNDS]: true,
	[Permission.UPDATE_REFUNDS]: true,
	[Permission.DELETE_REFUNDS]: true,
	[Permission.VIEW_ASSESSMENT_SCHEDULES]: true,
	[Permission.CREATE_ASSESSMENT_SCHEDULES]: true,
	[Permission.UPDATE_ASSESSMENT_SCHEDULES]: true,
	[Permission.DELETE_ASSESSMENT_SCHEDULES]: true,
	[Permission.VIEW_LATE_FEE_RULES]: true,
	[Permission.CREATE_LATE_FEE_RULES]: true,
	[Permission.UPDATE_LATE_FEE_RULES]: true,
	[Permission.DELETE_LATE_FEE_RULES]: true,
	[Permission.VIEW_ACCOUNT_NOTES]: true,
	[Permission.CREATE_ACCOUNT_NOTES]: true,
	[Permission.UPDATE_ACCOUNT_NOTES]: true,
	[Permission.DELETE_ACCOUNT_NOTES]: true,
	[Permission.VIEW_ACCOUNTING_REPORTS]: true,
	[Permission.VIEW_GL_CODES]: true,
	[Permission.CREATE_GL_CODES]: true,
	[Permission.UPDATE_GL_CODES]: true,
	[Permission.DELETE_GL_CODES]: true,
	[Permission.VIEW_VENDORS]: true,
	[Permission.CREATE_VENDORS]: true,
	[Permission.UPDATE_VENDORS]: true,
	[Permission.DELETE_VENDORS]: true,
	[Permission.VIEW_BANK_ACCOUNTS]: true,
	[Permission.CREATE_BANK_ACCOUNTS]: true,
	[Permission.UPDATE_BANK_ACCOUNTS]: true,
	[Permission.DELETE_BANK_ACCOUNTS]: true,
	[Permission.VIEW_BILLS]: true,
	[Permission.CREATE_BILLS]: true,
	[Permission.UPDATE_BILLS]: true,
	[Permission.DELETE_BILLS]: true,
	[Permission.PAY_BILLS]: true,
	[Permission.VIEW_INVOICES]: true,
	[Permission.CREATE_INVOICES]: true,
	[Permission.UPDATE_INVOICES]: true,
	[Permission.DELETE_INVOICES]: true,
	[Permission.VIEW_RECONCILIATIONS]: true,
	[Permission.CREATE_RECONCILIATIONS]: true,
	[Permission.UPDATE_RECONCILIATIONS]: true,
	[Permission.DELETE_RECONCILIATIONS]: true,
	[Permission.VIEW_JOURNAL_ENTRIES]: true,
	[Permission.CREATE_JOURNAL_ENTRIES]: true,
	[Permission.UPDATE_JOURNAL_ENTRIES]: true,
	[Permission.DELETE_JOURNAL_ENTRIES]: true,
	[Permission.VIEW_BUDGETS]: true,
	[Permission.CREATE_BUDGETS]: true,
	[Permission.UPDATE_BUDGETS]: true,
	[Permission.DELETE_BUDGETS]: true,
	[Permission.VIEW_BANK_IMPORTS]: true,
	[Permission.MANAGE_BANK_IMPORTS]: true,
	[Permission.VIEW_COLLECTIONS]: true,
	[Permission.MANAGE_COLLECTIONS]: true,
	[Permission.VIEW_ITEMS]: true,
	[Permission.CREATE_ITEMS]: true,
	[Permission.UPDATE_ITEMS]: true,
	[Permission.DELETE_ITEMS]: true,
};

// Accounting access for every non-Admin/non-Accounting role that has the
// accounting feature flag enabled. Rule: view everything, add / edit notes,
// nothing else. Modifying accounts, people, or money-movement records is
// restricted to Admin + Accounting roles.
const ACCOUNTING_VIEWER_WITH_NOTES: Partial<Record<Permission, boolean>> = {
	// View-only on structural / money records
	[Permission.VIEW_PEOPLE]: true,
	[Permission.CREATE_PEOPLE]: false,
	[Permission.UPDATE_PEOPLE]: false,
	[Permission.DELETE_PEOPLE]: false,
	// NOTE: MANAGE_WORK_ORDER_OWNER_CONTACT is intentionally NOT revoked here, so
	// viewer-with-notes roles (PM/Support/Maintenance) keep DEFAULT's `true`. That
	// endpoint is a narrow, server-constrained "fill a blank owner phone / create
	// the missing owner" action — not general people/account write access (#285).
	[Permission.VIEW_ACCOUNTS]: true,
	[Permission.CREATE_ACCOUNTS]: false,
	[Permission.UPDATE_ACCOUNTS]: false,
	[Permission.DELETE_ACCOUNTS]: false,
	[Permission.VIEW_CHARGES]: true,
	[Permission.CREATE_CHARGES]: false,
	[Permission.UPDATE_CHARGES]: false,
	[Permission.DELETE_CHARGES]: false,
	[Permission.VIEW_PAYMENTS]: true,
	[Permission.CREATE_PAYMENTS]: false,
	[Permission.UPDATE_PAYMENTS]: false,
	[Permission.DELETE_PAYMENTS]: false,
	[Permission.VIEW_CREDITS]: true,
	[Permission.CREATE_CREDITS]: false,
	[Permission.UPDATE_CREDITS]: false,
	[Permission.DELETE_CREDITS]: false,
	[Permission.VIEW_REFUNDS]: true,
	[Permission.CREATE_REFUNDS]: false,
	[Permission.UPDATE_REFUNDS]: false,
	[Permission.DELETE_REFUNDS]: false,
	[Permission.VIEW_ASSESSMENT_SCHEDULES]: true,
	[Permission.CREATE_ASSESSMENT_SCHEDULES]: false,
	[Permission.UPDATE_ASSESSMENT_SCHEDULES]: false,
	[Permission.DELETE_ASSESSMENT_SCHEDULES]: false,
	[Permission.VIEW_LATE_FEE_RULES]: true,
	[Permission.CREATE_LATE_FEE_RULES]: false,
	[Permission.UPDATE_LATE_FEE_RULES]: false,
	[Permission.DELETE_LATE_FEE_RULES]: false,
	// Notes: every role with the feature flag can add + edit notes. Delete
	// stays admin/accounting-only so notes become an append-mostly trail.
	[Permission.VIEW_ACCOUNT_NOTES]: true,
	[Permission.CREATE_ACCOUNT_NOTES]: true,
	[Permission.UPDATE_ACCOUNT_NOTES]: true,
	[Permission.DELETE_ACCOUNT_NOTES]: false,
	[Permission.VIEW_ACCOUNTING_REPORTS]: true,
	// GL codes / vendors: read-only for viewer-with-notes roles.
	[Permission.VIEW_GL_CODES]: true,
	[Permission.CREATE_GL_CODES]: false,
	[Permission.UPDATE_GL_CODES]: false,
	[Permission.DELETE_GL_CODES]: false,
	[Permission.VIEW_VENDORS]: true,
	[Permission.CREATE_VENDORS]: false,
	[Permission.UPDATE_VENDORS]: false,
	[Permission.DELETE_VENDORS]: false,
	[Permission.VIEW_BANK_ACCOUNTS]: true,
	[Permission.CREATE_BANK_ACCOUNTS]: false,
	[Permission.UPDATE_BANK_ACCOUNTS]: false,
	[Permission.DELETE_BANK_ACCOUNTS]: false,
	[Permission.VIEW_BILLS]: true,
	[Permission.CREATE_BILLS]: false,
	[Permission.UPDATE_BILLS]: false,
	[Permission.DELETE_BILLS]: false,
	[Permission.PAY_BILLS]: false,
	[Permission.VIEW_INVOICES]: true,
	[Permission.CREATE_INVOICES]: false,
	[Permission.UPDATE_INVOICES]: false,
	[Permission.DELETE_INVOICES]: false,
	[Permission.VIEW_RECONCILIATIONS]: true,
	[Permission.CREATE_RECONCILIATIONS]: false,
	[Permission.UPDATE_RECONCILIATIONS]: false,
	[Permission.DELETE_RECONCILIATIONS]: false,
	[Permission.VIEW_JOURNAL_ENTRIES]: true,
	[Permission.CREATE_JOURNAL_ENTRIES]: false,
	[Permission.UPDATE_JOURNAL_ENTRIES]: false,
	[Permission.DELETE_JOURNAL_ENTRIES]: false,
	[Permission.VIEW_BUDGETS]: true,
	[Permission.CREATE_BUDGETS]: false,
	[Permission.UPDATE_BUDGETS]: false,
	[Permission.DELETE_BUDGETS]: false,
	[Permission.VIEW_BANK_IMPORTS]: true,
	[Permission.MANAGE_BANK_IMPORTS]: false,
	[Permission.VIEW_COLLECTIONS]: true,
	[Permission.MANAGE_COLLECTIONS]: false,
	// Items: read-only for viewer-with-notes roles.
	[Permission.VIEW_ITEMS]: true,
	[Permission.CREATE_ITEMS]: false,
	[Permission.UPDATE_ITEMS]: false,
	[Permission.DELETE_ITEMS]: false,
};

// Permission matrix: role -> permission -> allowed
/**
 * ── THE MODEL (#500) ──
 *
 * `role = admin` is the explicit super administrator: it grants ALL permissions outright, a
 * genuine bypass rather than a very full matrix row. Everything else elevated is a NAMED
 * per-user capability, spelled out next to sql_admin and the rest, instead of one magic
 * `is_admin` column meaning "some unspecified amount of admin". That column is retired here —
 * it used to resolve its holder against the entire Admin matrix, 71 extra permissions for a
 * property manager and 99 for a contractor, including the whole accounting write set.
 *
 * So the resolution is:
 *
 *     role === Admin  → true, always
 *     otherwise       → rolePermissions[p] || grantedByCapability(user, p)
 *
 * which collapsed the `role === Admin && isAdmin` "super admin" idiom used across the
 * destructive routes down to `role === Admin`.
 *
 * `IMPERSONATE_USERS` sits under `user_admin` — a user administrator signs in as people, that
 * IS the job, and `api/assume.ts` gates on exactly it. It is full account takeover, and the one
 * place the is_admin retirement widened access rather than withdrawing it.
 *
 * Every change to what follows is reviewed against the characterization snapshot in
 * tests/api/permissions.matrix-snapshot.spec.ts, so a withdrawn grant is a diff line someone
 * has to justify.
 */
/**
 * Which permissions each named capability grants, on top of the holder's own role.
 *
 * ── WHY A TABLE AND NOT THREE IF-STATEMENTS ──
 *
 * `user_admin` shipped as a special case inside `hasPermission`. The moment a SECOND capability
 * grants permissions, a special case per capability is a guarantee that one of them eventually
 * checks the flag in a slightly different way — with the floor, without it, before the role
 * lookup instead of after. One table, one resolution path, and adding a capability is a data
 * change.
 *
 * EXHAUSTIVE on purpose (`Record`, not `Partial<Record>`): a new `FeatureFlagName` will not
 * compile until it says what it grants, and `[]` is the explicit way to say "nothing
 * individually". The access flags gate whole AREAS of the app — the accounting gate 404s every
 * accounting route without `accounting` — which is enforced at the route, not here.
 */
export const CAPABILITY_PERMISSIONS: Record<FeatureFlagName, readonly Permission[]> = {
	// Access flags: they open an area, they grant no individual permission.
	[FeatureFlagName.TasksEnabled]: [],
	[FeatureFlagName.Accounting]: [],
	[FeatureFlagName.Inbox]: [],

	// Capabilities whose whole effect is an area gate plus an admin-only screen.
	[FeatureFlagName.SqlAdmin]: [],
	[FeatureFlagName.EmailTemplatesAdmin]: [],
	[FeatureFlagName.ApplicationRulesAdmin]: [],
	[FeatureFlagName.CheatSheetAdmin]: [],

	/**
	 * `DELETE_LOCATION_EVENTS` rides here because a location event is a record ABOUT a staff
	 * member — the check-in trail a user administrator already reads across the org — not a
	 * work-order artefact.
	 */
	[FeatureFlagName.UserAdmin]: [
		Permission.CREATE_USERS,
		Permission.UPDATE_USERS,
		Permission.DELETE_USERS,
		Permission.IMPERSONATE_USERS,
		Permission.DELETE_LOCATION_EVENTS,
	],

	/**
	 * Create, edit and archive. NOT `DELETE_ASSOCIATIONS`: archiving is reversible and is what
	 * the office actually does with a community that has left, while a hard delete is
	 * unrecoverable and stays with the Admin role. Archive needs no permission of its own —
	 * `PUT /associations/:id` sets `archived_at` and cascades it to the work orders.
	 *
	 * `MANAGE_DOCUMENT_STRUCTURE` is the permission that was named for exactly this grant (see
	 * its docblock): pinning a community to its drive folder and onboarding one FROM a folder
	 * are association administration. Without it the Dropbox pin on `PUT /associations/:id` is
	 * silently dropped for the very people who manage associations.
	 *
	 * The inspection deletes are association-scoped records, so they follow the same holder.
	 */
	[FeatureFlagName.AssociationAdmin]: [
		Permission.CREATE_ASSOCIATIONS,
		Permission.UPDATE_ASSOCIATIONS,
		Permission.MANAGE_DOCUMENT_STRUCTURE,
		Permission.DELETE_INSPECTION_ISSUES,
		Permission.DELETE_INSPECTION_REPORTS,
		Permission.DELETE_INSPECTION_REPORT_ISSUES,
	],

	/**
	 * Delete work orders and the cost rows hanging off them. Every non-contractor role already
	 * creates and updates work orders; a holder who may delete the work order itself but not a
	 * mistaken time entry or misc cost on it would have to delete the whole order to fix a line.
	 */
	[FeatureFlagName.WorkOrderAdmin]: [
		Permission.DELETE_WORK_ORDERS,
		Permission.DELETE_TIME_ENTRIES,
		Permission.DELETE_MISC_COSTS,
		Permission.DELETE_MATERIALS,
		Permission.DELETE_MATERIAL_COSTS,
	],

	/**
	 * The full accounting write set — every permission `ACCOUNTING_VIEWER_WITH_NOTES` denies.
	 *
	 * DERIVED from that overlay rather than listed, so the two can never disagree. Listing it
	 * by hand is how you get a capability that grants nine of eleven writes and leaves two
	 * screens rendering a Save button that 403s — which is the exact bug this flag exists to
	 * fix (a property manager offered the association Dues/Late-fees sections by the
	 * `accounting` FLAG, then refused by the `update_accounts` PERMISSION).
	 *
	 * Adding a permission to the overlay therefore extends this automatically.
	 */
	[FeatureFlagName.AccountingAdmin]: (
		Object.entries(ACCOUNTING_VIEWER_WITH_NOTES) as [Permission, boolean][]
	)
		.filter(([, allowed]) => allowed === false)
		.map(([permission]) => permission),
};

/**
 * The ROLE PERMISSION a capability holder must ALSO have, from their own matrix row.
 *
 * A capability with no floor outranks the matrix: it says "this power, to anyone", and the
 * role table stops meaning anything. The floor is the sanity check — you may only be handed
 * the admin-level power over a thing you can already work with.
 *
 * `user_admin` has none, deliberately and unchanged: `VIEW_USERS` would be the obvious floor
 * and Contractor and Maintenance are both denied it, which would make the capability
 * ungrantable to exactly the people it was introduced (#500) to carry forward. Its containment
 * comes from the narrowness of the set instead.
 *
 * NOTE what the floor does NOT do: contractors hold `VIEW_ASSOCIATIONS` like every other role,
 * so `association_admin`'s floor does not exclude them. "A contractor holds no capability" is
 * enforced where the flag is GRANTED — `api/users.ts` and `PUT /admin/feature-flags` refuse it
 * on both write paths — which is the same posture `user_admin` has had since #500.
 */
/**
 * A FLAG the holder must ALSO carry for a capability to grant anything.
 *
 * Distinct from `CAPABILITY_FLOORS`, which is a ROLE-matrix permission. `accounting_admin`
 * documents itself as "useless without the accounting flag, since every accounting endpoint
 * runs ensureAccountingFlag first" — but that was only true of endpoints that run the gate.
 * `/time-entries` never does, so the capability granted cross-user reassignment to someone
 * who had never been given the accounting area at all. Enforcing the prerequisite HERE makes
 * the sentence true everywhere rather than at the endpoints that happen to check.
 */
export const CAPABILITY_PREREQUISITE_FLAGS: Partial<Record<FeatureFlagName, FeatureFlagName>> = {
	[FeatureFlagName.AccountingAdmin]: FeatureFlagName.Accounting,
};

export const CAPABILITY_FLOORS: Partial<Record<FeatureFlagName, Permission>> = {
	[FeatureFlagName.AssociationAdmin]: Permission.VIEW_ASSOCIATIONS,
	[FeatureFlagName.WorkOrderAdmin]: Permission.UPDATE_WORK_ORDERS,
	/**
	 * Every role holds `VIEW_ACCOUNTS`, so this floor excludes nobody on its own — the same
	 * shape as `user_admin`'s deliberate absence of one. Containment comes from elsewhere:
	 * the holder also needs the `accounting` flag, because every accounting endpoint runs
	 * `ensureAccountingFlag` before any permission check.
	 */
	[FeatureFlagName.AccountingAdmin]: Permission.VIEW_ACCOUNTS,
};

/**
 * What `user_admin` grants — kept as a named export because it is the set #500 is written
 * about, and `utils/permission-matrix.ts` and its specs read it by that name.
 *
 * Derived from the table rather than declared beside it, so the two cannot disagree.
 */
export const ELEVATED_PERMISSIONS: readonly Permission[] = CAPABILITY_PERMISSIONS[FeatureFlagName.UserAdmin];

const PERMISSION_MATRIX: Record<UserRole, Record<Permission, boolean>> = {
	// Admin has all permissions
	[UserRole.Admin]: {
		...DEFAULT_PERMISSIONS,
	},

	[UserRole.PropertyManager]: {
		...DEFAULT_PERMISSIONS,
		// Property managers can manage most things but not users
		[Permission.CREATE_USERS]: false,
		[Permission.UPDATE_USERS]: false,
		[Permission.DELETE_USERS]: false,

		// They also can not delete records
		[Permission.DELETE_WORK_ORDERS]: false,
		[Permission.DELETE_ASSOCIATIONS]: false,
		[Permission.DELETE_TIME_ENTRIES]: false,
		[Permission.DELETE_MATERIALS]: false,
		[Permission.DELETE_MATERIAL_COSTS]: false,
		[Permission.DELETE_MISC_COSTS]: false,
		[Permission.DELETE_LOCATION_EVENTS]: false,
		[Permission.DELETE_INSPECTION_ISSUES]: false,
		[Permission.DELETE_INSPECTION_REPORTS]: false,
		[Permission.DELETE_INSPECTION_REPORT_ISSUES]: false,

		// Or impersonate users
		[Permission.IMPERSONATE_USERS]: false,

		// Applications: PMs create/update/review but don't hard-delete.
		[Permission.DELETE_EXTERIOR_APPLICATIONS]: false,

		/**
		 * A PM is the APPROVER, never the reviewer (#496).
		 *
		 * Letting them mark their own work orders ready would collapse the two-person
		 * check into one click: mark ready, approve, done — and the customer-facing
		 * rewrite that the whole feature exists to guarantee never happens. Accounting
		 * being out for the day is not a reason to hand the PM both halves; an admin can
		 * review, and the send-back path exists for when they disagree.
		 */
		[Permission.REVIEW_WORK_ORDERS]: false,

		// Accounting: view everything + add/edit notes. Money writes are
		// admin/accounting-only (see ACCOUNTING_VIEWER_WITH_NOTES above).
		...ACCOUNTING_VIEWER_WITH_NOTES,
	},


	[UserRole.Maintenance]: {
		...DEFAULT_PERMISSIONS,
		// Maintenance can not manage users
		[Permission.CREATE_USERS]: false,
		[Permission.UPDATE_USERS]: false,
		[Permission.DELETE_USERS]: false,

		// They also can not delete records
		[Permission.DELETE_WORK_ORDERS]: false,
		[Permission.DELETE_ASSOCIATIONS]: false,
		[Permission.DELETE_TIME_ENTRIES]: false,
		[Permission.DELETE_MATERIALS]: false,
		[Permission.DELETE_MATERIAL_COSTS]: false,
		[Permission.DELETE_MISC_COSTS]: false,
		[Permission.DELETE_LOCATION_EVENTS]: false,
		[Permission.DELETE_INSPECTION_ISSUES]: false,
		[Permission.DELETE_INSPECTION_REPORTS]: false,
		[Permission.DELETE_INSPECTION_REPORT_ISSUES]: false,

		// Limited task permissions
		[Permission.DELETE_TASKS]: false,

		// Or impersonate users
		[Permission.IMPERSONATE_USERS]: false,

		// Applications: maintenance can create/update but not hard-delete.
		[Permission.DELETE_EXTERIOR_APPLICATIONS]: false,

		// Maintenance AUTHORS the internal completion note; accounting rewrites it for the
		// customer (#496). Self-reviewing would defeat the point — the text being
		// sanitised is their own.
		[Permission.REVIEW_WORK_ORDERS]: false,

		// Accounting: with the accounting feature flag, maintenance can view
		// and add/edit notes but cannot modify money/people.
		...ACCOUNTING_VIEWER_WITH_NOTES,
	},

	[UserRole.Contractor]: {
		...DEFAULT_PERMISSIONS,
		// Contractors can only work with work orders assigned to them. They can view
		// and update their own work orders, upload attachments, and log time/materials.
		// Everything else is off — including creating work orders or associations.

		[Permission.VIEW_USERS]: false,
		[Permission.CREATE_USERS]: false,
		[Permission.UPDATE_USERS]: false,
		[Permission.DELETE_USERS]: false,

		// UPDATE_WORK_ORDERS is denied at the matrix level; the work-orders PUT handler
		// grants the update path explicitly via `canEditAsContractor` when assigned_to matches.
		[Permission.CREATE_WORK_ORDERS]: false,
		[Permission.UPDATE_WORK_ORDERS]: false,
		[Permission.DELETE_WORK_ORDERS]: false,
		// An outside contractor must never author the customer-facing text (#496).
		[Permission.REVIEW_WORK_ORDERS]: false,
		// Contractors only touch their assigned WOs — no owner-contact writes.
		[Permission.MANAGE_WORK_ORDER_OWNER_CONTACT]: false,

		[Permission.CREATE_ASSOCIATIONS]: false,
		[Permission.UPDATE_ASSOCIATIONS]: false,
		[Permission.DELETE_ASSOCIATIONS]: false,
		// Contractors can READ the document browser (VIEW_ASSOCIATIONS, which every role
		// holds) but must not file into an owner's document folder. This is the ONLY role the
		// permission is revoked for — internal staff, Support included, need to upload.
		[Permission.UPLOAD_DOCUMENTS]: false,

		[Permission.DELETE_TIME_ENTRIES]: false,
		[Permission.DELETE_MATERIALS]: false,

		// No access to the material-cost or shortcut-code catalogs.
		[Permission.CREATE_MATERIAL_COSTS]: false,
		[Permission.UPDATE_MATERIAL_COSTS]: false,
		[Permission.DELETE_MATERIAL_COSTS]: false,
		[Permission.DELETE_MISC_COSTS]: false,
		[Permission.DELETE_LOCATION_EVENTS]: false,

		[Permission.VIEW_INSPECTION_ISSUES]: false,
		[Permission.CREATE_INSPECTION_ISSUES]: false,
		[Permission.UPDATE_INSPECTION_ISSUES]: false,
		[Permission.DELETE_INSPECTION_ISSUES]: false,
		[Permission.VIEW_INSPECTION_REPORTS]: false,
		[Permission.CREATE_INSPECTION_REPORTS]: false,
		[Permission.UPDATE_INSPECTION_REPORTS]: false,
		[Permission.DELETE_INSPECTION_REPORTS]: false,
		[Permission.VIEW_INSPECTION_REPORT_ISSUES]: false,
		[Permission.CREATE_INSPECTION_REPORT_ISSUES]: false,
		[Permission.UPDATE_INSPECTION_REPORT_ISSUES]: false,
		[Permission.DELETE_INSPECTION_REPORT_ISSUES]: false,

		// Contractors can READ shortcut codes (needed for association details on their
		// assigned work orders) but cannot manage them.
		[Permission.CREATE_ASSOCIATION_SHORTCUT_CODES]: false,
		[Permission.UPDATE_ASSOCIATION_SHORTCUT_CODES]: false,
		[Permission.DELETE_ASSOCIATION_SHORTCUT_CODES]: false,

		[Permission.VIEW_TASKS]: false,
		[Permission.CREATE_TASKS]: false,
		[Permission.UPDATE_TASKS]: false,
		[Permission.DELETE_TASKS]: false,

		[Permission.IMPERSONATE_USERS]: false,

		// Applications: contractors have no access to architectural review.
		[Permission.VIEW_EXTERIOR_APPLICATIONS]: false,
		[Permission.CREATE_EXTERIOR_APPLICATIONS]: false,
		[Permission.UPDATE_EXTERIOR_APPLICATIONS]: false,
		[Permission.DELETE_EXTERIOR_APPLICATIONS]: false,

		// Accounting: with the feature flag, contractors can view and add/edit
		// notes but cannot modify money/people.
		...ACCOUNTING_VIEWER_WITH_NOTES,
	},

	[UserRole.Accounting]: {
		...DEFAULT_PERMISSIONS,
		// Accounting can view and manage financial data

		// REVIEW_WORK_ORDERS is inherited from the baseline and belongs here: this role IS
		// the reviewer (#496). Left implicit rather than restated so there is one place
		// that grants it — but noted, because "which roles can review" is the question
		// this matrix will be read for.

		// But not manage users
		[Permission.CREATE_USERS]: false,
		[Permission.UPDATE_USERS]: false,
		[Permission.DELETE_USERS]: false,

		// Limited task permissions
		[Permission.DELETE_TASKS]: false,

		// Or impersonate users
		[Permission.IMPERSONATE_USERS]: false,
	},

	[UserRole.Support]: {
		...DEFAULT_PERMISSIONS,
		// Support can also read most data

		// no editing associations
		[Permission.CREATE_ASSOCIATIONS]: false,
		[Permission.UPDATE_ASSOCIATIONS]: false,
		[Permission.DELETE_ASSOCIATIONS]: false,

		// No editing users
		[Permission.CREATE_USERS]: false,
		[Permission.UPDATE_USERS]: false,
		[Permission.DELETE_USERS]: false,

		// Or impersonate users
		[Permission.IMPERSONATE_USERS]: false,

		// Support reads; it does not author what a customer will be invoiced from (#496).
		[Permission.REVIEW_WORK_ORDERS]: false,

		// Accounting: view everything + add/edit notes (money writes are
		// admin/accounting-only).
		...ACCOUNTING_VIEWER_WITH_NOTES,
	},
};

/** Anything `hasPermission` will answer for: a role permission, or a capability flag. */
export type AuthSubject = Permission | FeatureFlagName | string;

/**
 * The shape `hasPermission` needs. Structural on purpose, so a D1 row, a session user and a
 * test literal all satisfy it without adapters.
 *
 * `flags` accepts BOTH shapes in circulation: the client holds a `Record<string, boolean>`
 * from `/me/feature-flags`, the server naturally builds a list. Making callers normalise
 * would guarantee that someone eventually passes the wrong one and gets a silent false.
 */
export interface AuthUserLike {
	id?: string;
	role?: string;
	isAdmin?: boolean;
	flags?: Record<string, boolean> | readonly string[] | null;
}

function holdsFlag(user: AuthUserLike, flag: string): boolean {
	const f = user.flags;
	if (!f) return false;
	return Array.isArray(f) ? f.includes(flag) : !!(f as Record<string, boolean>)[flag];
}

/**
 * Check if a user has permission to perform an action
 */
export function hasPermission(
	subject: AuthSubject,
	who?: AuthUserLike | PermissionContext | null,
): boolean {
	// Accepts the user directly OR the legacy `{ user }` context, so the ~250 existing call
	// sites keep working while new ones use the shorter form. Detected structurally rather
	// than by an overload signature, because a `User` never has a `.user` property.
	const user = (who && typeof who === 'object' && 'user' in who)
		? (who as PermissionContext).user as AuthUserLike | null | undefined
		: who as AuthUserLike | null | undefined;

	// Deny by default. `undefined` is what a half-loaded frontend session looks like, and it
	// must never fall through to a permissive branch.
	if (!user) return false;

	const isPermission = (Object.values(Permission) as string[]).includes(subject);
	if (!isPermission) {
		/**
		 * A capability flag.
		 *
		 * The Admin ROLE passes without holding it — that is the target model, and it already
		 * matches `isAdminOrFlagged`, which documents "admins always pass regardless". Safe to
		 * implement now rather than at the switch-over because flags are a NEW subject type
		 * here: no existing call site asks `hasPermission` a flag question, so nothing changes
		 * behaviour today.
		 */
		if (user.role === UserRole.Admin) return true;
		return holdsFlag(user, subject);
	}

	/**
	 * THE SWITCH-OVER (#500): the Admin ROLE is the only bypass, and `is_admin` grants nothing.
	 *
	 * Until this commit the `is_admin` column resolved its holder against the whole Admin
	 * matrix — 71 extra permissions for a property manager, 99 for a contractor, including the
	 * entire accounting write set. The column is retired: it is no longer read here at all, so
	 * a row that still carries it behaves exactly like one that does not. User administration
	 * is the `user_admin` capability, below.
	 */
	if (user.role === UserRole.Admin) return true;

	const rolePermissions = PERMISSION_MATRIX[user.role as UserRole];
	if (rolePermissions?.[subject as Permission]) return true;

	return grantedByCapability(user, subject, rolePermissions);
}

/**
 * The CORE admin capabilities: the three that carry real operational authority.
 *
 * Deliberately not every flag. `email_templates_admin` and `cheat_sheet_admin` are authority
 * over WORDING; `sql_admin` is its own, stricter thing; the access flags (`accounting`,
 * `inbox`, `tasks_enabled`) open an area and grant nothing. Mirrors `hasAdminCapability` in
 * `hooks/use-permissions.ts` — the nav and the routes must answer this question the same way
 * or a row appears that its own destination refuses.
 */
/**
 * Who may drive the DOCUMENT OPERATOR routes — provision, archive, owner-index, reindex.
 *
 * Narrower than {@link ADMIN_TOOL_CAPABILITIES} on purpose. Those are read-across-the-org
 * tools; these MUTATE the one shared company Dropbox, and they are keyed to associations —
 * a folder tree per community — so association administration is the capability that fits.
 *
 * `work_order_admin` is deliberately absent. Its registry description bounds it to work orders
 * (their deletes and Date of Order) "and nothing else", and a capability that narrow must not
 * reach a destructive external-storage operation on every community at once.
 */
export const DOCUMENT_OPERATOR_CAPABILITIES: readonly FeatureFlagName[] = [
	FeatureFlagName.UserAdmin,
	FeatureFlagName.AssociationAdmin,
];

export const ADMIN_TOOL_CAPABILITIES: readonly FeatureFlagName[] = [
	FeatureFlagName.UserAdmin,
	FeatureFlagName.AssociationAdmin,
	FeatureFlagName.WorkOrderAdmin,
];

/**
 * May this user open the OPERATIONAL admin tools — the audit log, the location history, the
 * document operator surfaces?
 *
 * These read across the whole organisation and destroy nothing, which is a different question
 * from `role === Admin` (Backup, Restore, SQL: read or overwrite every table) and a different
 * question again from any single named permission.
 *
 * ── WHY THIS EXISTS ──
 *
 * The routes it replaces tested `user.isAdmin`, the column #500 retired. That is wrong in BOTH
 * directions, as `api/documents/routes.ts` has noted in a comment since the retirement: it
 * refuses an Admin-role user whose row never carried the column, and it admits a contractor
 * whose row still does. Neither is a judgement anyone made; both are leftovers.
 *
 * Contractors are excluded outright. A contractor's whole model is scoped to the work orders
 * they were assigned, and no capability flag is meant to lift that.
 */
/** Document operator rights — see {@link DOCUMENT_OPERATOR_CAPABILITIES}. */
export function hasDocumentOperatorCapability(who?: AuthUserLike | PermissionContext | null): boolean {
	const user = (who && typeof who === 'object' && 'user' in who)
		? (who as PermissionContext).user as AuthUserLike | null | undefined
		: who as AuthUserLike | null | undefined;
	if (!user) return false;
	if (user.role === UserRole.Contractor) return false;
	if (user.role === UserRole.Admin) return true;
	return DOCUMENT_OPERATOR_CAPABILITIES.some((flag) => holdsFlag(user, flag));
}

export function hasAdminCapability(who?: AuthUserLike | PermissionContext | null): boolean {
	const user = (who && typeof who === 'object' && 'user' in who)
		? (who as PermissionContext).user as AuthUserLike | null | undefined
		: who as AuthUserLike | null | undefined;

	if (!user) return false;
	if (user.role === UserRole.Contractor) return false;
	if (user.role === UserRole.Admin) return true;
	return ADMIN_TOOL_CAPABILITIES.some((flag) => holdsFlag(user, flag));
}

/**
 * Whether a NAMED CAPABILITY the user holds grants this permission (#500, association +
 * work-order capabilities).
 *
 * Reached only after the role matrix has already said no, so a capability can add and never
 * subtract. Two conditions, both required:
 *
 *   1. the user holds the flag, and
 *   2. their ROLE holds the capability's floor permission (`CAPABILITY_FLOORS`).
 *
 * The floor is read from `rolePermissions`, NOT from `hasPermission`, on purpose: floors must
 * come from the role matrix alone. Resolving one through another capability would let two
 * flags chain into a grant neither of them names.
 */
function grantedByCapability(
	user: AuthUserLike,
	subject: AuthSubject,
	rolePermissions: Record<Permission, boolean> | undefined,
): boolean {
	for (const flag of Object.keys(CAPABILITY_PERMISSIONS) as FeatureFlagName[]) {
		if (!(CAPABILITY_PERMISSIONS[flag] as readonly string[]).includes(subject)) continue;
		if (!holdsFlag(user, flag)) continue;
		const prerequisite = CAPABILITY_PREREQUISITE_FLAGS[flag];
		if (prerequisite && !holdsFlag(user, prerequisite)) continue;
		const floor = CAPABILITY_FLOORS[flag];
		if (floor && !rolePermissions?.[floor]) continue;
		return true;
	}
	return false;
}

/** Every subject must pass — the shape of a write gate. */
export function hasAllPermissions(
	subjects: readonly AuthSubject[],
	who?: AuthUserLike | PermissionContext | null,
): boolean {
	return subjects.every((s) => hasPermission(s, who));
}

/** Any one of them — for a screen reachable through more than one capability. */
export function hasAnyPermission(
	subjects: readonly AuthSubject[],
	who?: AuthUserLike | PermissionContext | null,
): boolean {
	return subjects.some((s) => hasPermission(s, who));
}

/**
 * Check if a user can access a specific resource
 */
export function canAccessResource(
	permission: Permission,
	context: PermissionContext,
): boolean {
	return hasPermission(permission, context);
}

/**
 * Guards sub-resources (time/materials/files/attachments) so contractors can only
 * read/write rows tied to a work order they're assigned to. Non-contractors always
 * pass through. A missing workOrderId is treated as "blocked" for contractors so
 * list endpoints require a scoping filter.
 */
export async function isContractorBlockedFromWorkOrder(
	env: { DB: D1Database },
	user: User,
	workOrderId: string | null | undefined,
): Promise<boolean> {
	if (user.role !== UserRole.Contractor) return false;
	if (!workOrderId) return true;
	const row = await env.DB
		.prepare('SELECT assigned_to FROM work_orders WHERE id = ? AND deleted_at IS NULL')
		.bind(workOrderId)
		.first<{ assigned_to: string | null }>();
	return !row || row.assigned_to !== user.id;
}

/**
 * Whether a user is narrowed to the associations they manage (#411).
 *
 * Only PROPERTY MANAGERS are. Support and admin see the whole portfolio — that is the
 * job — and the other roles that can reach these surfaces at all (they need
 * VIEW_ACCOUNTS) are left alone rather than silently losing something they have today.
 */
export function isInboxScopedToOwnAssociations(user: User): boolean {
	// The Admin ROLE is portfolio-wide by fiat. No flag widens reach: `is_admin` is retired,
	// and `user_admin` administers accounts — it does not make one manager's inbox everyone's.
	if (user.role === UserRole.Admin) return false;
	return user.role === UserRole.PropertyManager;
}

export interface InboxScope {
	/** A predicate on a `messages` row aliased `m`, or null when the user sees everything. */
	clause: string | null;
	binds: unknown[];
}

/**
 * The scope predicate, for the list, the label counts and the single-message read alike.
 *
 * All three MUST use this. `utils/inbox-triage.ts` exists because the drawer badge, the
 * rail and the list once computed the queue independently and disagreed; scoping only
 * some of them recreates exactly that.
 *
 * TWO limbs, and the second is load-bearing: the associations you manage, OR whatever has
 * your name on it. #411 gave only the first, which meant mail matching no association —
 * `association_id` is NULL, and NULL never satisfies `IN` — was invisible to every
 * property manager, so it could not be handed to one at all. That is exactly the mail
 * that needs a human name on it, and "file it to an association first" asks the assigner
 * to answer the question they are trying to delegate.
 *
 * It is not a back door. Assignment is a WRITE, gated by this same predicate, so nobody
 * can hand themselves anything: the grant is always somebody who can already see a
 * message choosing to pass that one message on. And it is per MESSAGE — being given one
 * piece of an association's mail does not open the rest of it.
 *
 * Parenthesised because callers interpolate this straight into an `AND` chain. Without
 * the brackets the `OR` would swallow every other condition in the query — including
 * `deleted_at IS NULL`.
 */
export function inboxScopeForUser(user: User): InboxScope {
	const scope = associationScopeForUser(user, 'm');
	if (!scope.clause) return scope;
	return {
		clause: `(${scope.clause} OR m.assigned_to = ?)`,
		binds: [...scope.binds, user.id],
	};
}

/**
 * The same predicate for any table with an `association_id`, given its alias.
 *
 * Exterior Improvements needs it against `ea`, work-order style reports against theirs.
 * Parameterised rather than copied: the rule for "which associations are mine" must have
 * exactly one definition, or the two features drift and a manager sees a request on a
 * screen that the inbox hides.
 *
 * @param alias the table alias carrying `association_id` in the surrounding query
 */
export function associationScopeForUser(user: User, alias: string): InboxScope {
	if (!isInboxScopedToOwnAssociations(user)) return { clause: null, binds: [] };
	return {
		clause: `${alias}.association_id IN (SELECT id FROM associations WHERE assigned_to = ? AND deleted_at IS NULL)`,
		binds: [user.id],
	};
}

/**
 * May this user file things INTO the given association?
 *
 * Separate from {@link associationScopeForUser} because that one emits
 * `<alias>.association_id`, which is the FK on child tables — the associations table's
 * own key is `id`. Used wherever a request names a destination association rather than
 * being read out of an existing row: re-routing a message, creating a request from one.
 *
 * True for everyone unscoped, so support and admin are unaffected.
 */
export async function canManageAssociation(
	env: { DB: D1Database },
	user: User,
	associationId: string,
): Promise<boolean> {
	if (!isInboxScopedToOwnAssociations(user)) return true;
	const row = await env.DB
		.prepare('SELECT 1 AS ok FROM associations WHERE id = ? AND assigned_to = ? AND deleted_at IS NULL')
		.bind(associationId, user.id)
		.first();
	return !!row;
}

export function contractorForbiddenResponse(): Response {
	return new Response(JSON.stringify({
		type: 'error',
		message: 'Contractors can only access work orders assigned to them',
		code: 'CONTRACTOR_NOT_ASSIGNED',
	}), {
		status: 403,
		headers: { 'content-type': 'application/json' },
	});
}

/**
 * Get permission error response
 */
export function getPermissionErrorResponse(permission: Permission): Response {
	return new Response(JSON.stringify({
		type: 'error',
		message: `Insufficient permissions for ${permission}`,
		code: 'INSUFFICIENT_PERMISSIONS',
	}), {
		status: 403,
		headers: { 'content-type': 'application/json' },
	});
}
